/**
 * TelefonoDellAzienda — il telefono di un'azienda nella scheda «Strumenti» della console (atlante cap. 264).
 *
 * Dice a colpo d'occhio a che punto è: i numeri che rispondono per l'azienda, la richiesta dell'amministratore che
 * aspetta la consegna (da quando, chi, come pagherà il giorno della consegna) e il bottone «Assegna il numero
 * all'azienda». Dopo un'assegnazione rilegge numeri, richiesta e scheda dell'azienda (lo strumento si è acceso).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { PhoneForwardedOutlined } from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { data, ultimoGiorno } from '../strumenti/testiStrumenti';
import { aziendaPerGliStrumenti, viaDiAttivazione } from '@shared/strumenti/attivazione';
import type { TenantConsoleListItem } from '../../services/tenantService';
import {
  STRUMENTO_TELEFONO, leggiNumeriDellAzienda, leggiRichiesteInAttesa, type AllaConsegna, type RichiestaInAttesa,
} from '../../services/consegneService';
import { comeHaChiesto, fraseAllaConsegna } from './testiConsegna';
import { DialogoAssegnaNumero } from './DialogoAssegnaNumero';

interface Props {
  tenant: TenantConsoleListItem;
  /** Dopo una consegna l'azienda è cambiata (strumento acceso): la scheda la rilegge. */
  onChange: () => void;
}

/** Cosa succederebbe consegnando oggi, dalla STESSA regola del server (`viaDiAttivazione`). */
function allaConsegnaDallAzienda(tenant: TenantConsoleListItem, oraMs: number): AllaConsegna {
  const via = viaDiAttivazione(aziendaPerGliStrumenti(tenant), oraMs);
  const gratis = via.via === 'nell_abbonamento' ? via.gratis_fino_al_ms : via.via === 'gratis_fino_a_fine_prova' ? via.fino_al_ms : null;
  return { via: via.via, gratis_fino_al: gratis === null ? null : new Date(gratis).toISOString() };
}

const giorno = (iso: string) => ultimoGiorno(Date.parse(iso));

export const TelefonoDellAzienda: React.FC<Props> = ({ tenant, onChange }) => {
  const [numeri, setNumeri] = useState<string[] | null>(null);
  const [richiesta, setRichiesta] = useState<RichiestaInAttesa | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState(false);

  const leggi = useCallback(async () => {
    setErrore(null);
    try {
      const [n, r] = await Promise.all([leggiNumeriDellAzienda(tenant.id), leggiRichiesteInAttesa()]);
      setNumeri(n);
      setRichiesta(r.find((x) => x.tenant_id === tenant.id && x.strumento === STRUMENTO_TELEFONO) ?? null);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
      setNumeri([]);
    }
  }, [tenant.id]);

  useEffect(() => { void leggi(); }, [leggi]);

  const allaConsegna = richiesta?.alla_consegna ?? allaConsegnaDallAzienda(tenant, Date.now());
  const consegna = fraseAllaConsegna(allaConsegna, giorno);

  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line, borderRadius: `${tokens.radius.lg}px` }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mr: 1 }}>Numero del centralino</Typography>
            {numeri === null ? (
              <CircularProgress size={14} />
            ) : numeri.length > 0 ? (
              <SoftChip label="risponde" tone="success" />
            ) : richiesta ? (
              <SoftChip label="in attesa del numero" tone="warning" />
            ) : (
              <SoftChip label="nessun numero" tone="default" />
            )}
          </Stack>
          {numeri && numeri.length > 0 && (
            <Typography sx={{ fontFamily: tokens.font.mono, mt: 0.5 }}>{numeri.join(' · ')}</Typography>
          )}
        </Box>
        <Button
          variant={richiesta ? 'contained' : 'outlined'}
          disableElevation
          startIcon={<PhoneForwardedOutlined />}
          onClick={() => setDialogo(true)}
          sx={{ textTransform: 'none', minHeight: 40, flexShrink: 0 }}
        >
          Assegna il numero all’azienda
        </Button>
      </Stack>

      {richiesta && (
        <Typography variant="body2" sx={{ mt: 1.5, color: tokens.color.graphite }}>
          Chiesto {richiesta.richiesta_il ? `il ${data(Date.parse(richiesta.richiesta_il))}` : ''}
          {richiesta.richiesta_da_nome ? ` da ${richiesta.richiesta_da_nome}` : ''} ({comeHaChiesto(richiesta.richiesta_via)}): serve la
          pratica del gestore e il numero.
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: tokens.color.ash }}>Il giorno della consegna</Typography>
      <Typography variant="body2" sx={{ color: consegna.attenzione ? tokens.color.warning : tokens.color.graphite }}>
        {consegna.testo}
      </Typography>
      {errore && <Alert severity="error" sx={{ mt: 1.5 }}>{errore}</Alert>}

      <DialogoAssegnaNumero
        aperto={dialogo}
        onChiudi={() => setDialogo(false)}
        tenantId={tenant.id}
        nomeAzienda={tenant.nome_azienda}
        allaConsegna={allaConsegna}
        onFatto={() => { void leggi(); onChange(); }}
      />
    </Paper>
  );
};
